<script>
	import Grid from '$lib/Components/Layout/Grid/Grid.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import ScrollNav from '$lib/Components/ScrollNav/ScrollNav.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import EDiagonalLeft from './images/IW-Laser-diagonal-extension-tool-full_window-left.svg';
	import FDiagonalRight from './images/IW-Laser-diagonal-extension-tool-full_window-right.svg';
	import laserToolHead from './images/LP-Illustrated-Measurement-Guide-01.png';
	import laserToolkit from './images/LP-Illustrated-Measurement-Guide-02.svg';
	import frameStep12 from './images/LP-Illustrated-Measurement-Guide-04.svg';
	import fromGlass from './images/LP-Illustrated-Measurement-Guide-05.svg';
	import AWidthTop from './images/LP-Illustrated-Measurement-Guide-06.svg';
	import BWidthTop from './images/LP-Illustrated-Measurement-Guide-07.svg';
	import CDWidth from './images/LP-Illustrated-Measurement-Guide-08.svg';
	import measureDiagonalTool from './images/LP-Illustrated-Measurement-Guide-09.svg';
	import measureDiagonal from './images/LP-Illustrated-Measurement-Guide-10.svg';
	import youtubeThumbnail from './images/Y3E8dnh-bow.jpg?w=610';
	import bowConcave from './images/bow_concave.svg';
	import bowConvex from './images/bow_convex.svg';
	import bowNone from './images/bow_none.svg';

	import gapValue from './images/LP-Illustrated-Measurement-Guide-15.svg';
	import measureForm from './images/LP-Illustrated-Measurement-Guide-16.jpg';
	import indowFigure from './images/LP-Illustrated-Measurement-Guide-17.svg';
	import returnLaserKit from './images/return-laser-kit.png';

	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import howtomeasurewithlaser from './images/LP-Illustrated-Measurement-Guide-19.svg';
	import laserToolValue from './images/LP-Illustrated-Measurement-Guide-20.png';
	import knowyourWindow from './images/LP-Illustrated-Measurement-Guide-21.svg';
	import windowHardware from './images/LP-Illustrated-Measurement-Guide-22.svg';
	import measurementMap from './images/LP-Illustrated-Measurement-Guide-22B.svg';
	import frameDepth from './images/LP-Illustrated-Measurement-Guide-23.svg';
	import inchesAndfeet from './images/LP-Illustrated-Measurement-Guide-24.png';
	import inchesOnly from './images/LP-Illustrated-Measurement-Guide-25.png';
</script>

<Section backgroundColor="blue" class="max-md:-mt-32 max-md:pt-16">
	<h1>A step-by-step guide to measuring your windows.</h1>
	<h2 class="text-lg text-black uppercase">
		How to be a laser measure master.
	</h2>
	<div class="col-span-full! w-full">
		<Image
			src={laserToolHead}
			class="max-w-full"
			alt="Hand holding laser measurement tool."
			loading="eager"
		></Image>
	</div>
</Section>

<Section backgroundColor="grey-light">
	<h2>Review Your Tools</h2>
	<ol class="w-auto list-decimal">
		<li><p><a href="#how-to-use-the-laser-tool">Laser</a></p></li>
		<li><p>Product Type Samples</p></li>
		<li><p>Compression Tube Color Samples</p></li>
		<li><p>Adjustable Diagonal Extension Tool</p></li>
		<li>
			<p><a href="/guides/laser-measurement-form">Paper Measurement Form</a></p>
		</li>
	</ol>
	<p>
		*See <a href="#appendix">appendix</a> for all uses of this tool & more measurement
		tips & terms.
	</p>
	<Image
		src={laserToolkit}
		alt="Diagram of the Laser Measurement Kit."
		class="no-bleed"
	/>
	<p>
		Other Tools to use: Online Portal - Record your measurements as you go on
		the Measure Form provided. Our online portal, Measure by Indow®, is where
		you record your final measurements. You should have received an access link
		via email. If you can’t find it, go to <a href="/measure-support"
			>Measure Support</a
		>
	</p>
	<p>
		Measurement Phone Support - We have in-house measuring experts dedicated to
		helping you measure your windows and resolving any issues you may run into.
		If you need assistance, we’re here to help: <a href="tel:5032842260"
			>503.284.2260</a
		>
	</p>
	<p>
		<strong>NOTE:</strong> Keep the USPS shipping box and return label the Laser Measuring
		Kit came with. You’ll need them to mail the kit back to Indow! Return all items
		so you’re not charged for them.
	</p>
</Section>
<Section backgroundColor="grey-light">
	<h2>How To Use A Laser Measure</h2>
	<Youtube
		src="https://www.youtube.com/watch?v=Y3E8dnh-bow"
		posterImageSrc={youtubeThumbnail}
		title="How to Measure Your Windows with Our Laser Tool"
	/>
</Section>

<ScrollNav>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#prepare-to-measure" class="scrollactive-item"
			>Prepare to Measure</a
		>
	</li>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#assessing-each-window" class="scrollactive-item"
			>Assessing Each Window</a
		>
	</li>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#how-to-use-the-laser-tool" class="scrollactive-item"
			>Using The Laser</a
		>
	</li>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#measure-windows" class="scrollactive-item">Measure Windows</a>
	</li>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#final-window-check" class="scrollactive-item"
			>Final Window Check</a
		>
	</li>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#submit-measurements" class="scrollactive-item"
			>Submit Measurements</a
		>
	</li>
</ScrollNav>

<Section id="prepare-to-measure">
	<h2>Prepare to Measure</h2>
	<p>
		Remove any furniture around the windows for easy access and a more accurate
		measurement. If you have blinds that have to be removed, now is a good time.
	</p>
	<p>
		Review the paper <a href="/guides/laser-measurement-form">Measure Form</a> included
		in the kit. Use this to list each window you measure. We suggest starting with
		the leftmost window and moving right.
	</p>
	<h3>Record:</h3>
	<ul class="w-auto">
		<li>Room and Location (as you want them to appear on the insert label)</li>
		<li>Product Type (Standard Grade, Acoustic Grade, Sleep Panels, etc.)</li>
		<li>Tubing Color</li>
	</ul>
	<p>
		If you were sent mullion* instructions by your Fit Specialist, refer to the
		<strong>Mullion Guide</strong> handout to install the L brackets before measuring.
	</p>
	<p>
		<strong>NOTE:</strong> Measuring a lot of windows? Start with 3 or 4, then
		enter measurements into the <strong>Measure by Indow</strong> portal to make
		sure they are accepted before continuing. *See
		<a href="#appendix">appendix</a> for more.
	</p>
	<Image
		src={frameStep12}
		alt="Diagram shows a window frame with two Frame Steps."
		class="no-bleed"
	/>
	<h3>Frame Step & Depth</h3>
	<p>
		Frame step is the flat surface within your window frame and where your
		insert will be installed. Frame depth* is how deep your frame step is. If
		you think of the window frame width as left to right, frame depth is front
		to back. Place your laser tool on your desired frame step and point straight
		across. Record the Frame Depth on your Measure Form. You do not need to
		include the Frame Step - this is for your reference only. *See <a
			href="#appendix">appendix</a
		> for full explanation of frame depth and fraction chart
	</p>
</Section>

<Section id="assessing-each-window">
	<h2>Assessing Each Window</h2>
	<h3>Label Windows</h3>
	<p>
		Label the window you’re measuring on the <strong>Measure Form</strong> so
		you can identify it when you enter measurements into the
		<strong>Measure by Indow</strong> portal. We use this information to create labels
		for your inserts so you know which insert goes where.
	</p>
	<p>
		<strong>Note:</strong> Please use a unique combination of room and location names
		for each window.
	</p>
	<h3>Measure Twice!</h3>
	<p>
		“Measure twice, cut once” applies here too! As we custom cut each insert to
		match your windows, we want to ensure a snug fit within your frames. For
		each window, we recommend measuring twice and filling out two rows of your
		<strong>Measure Form</strong>. If the measurements match or are within 1/16"
		of each other, scratch out a row and proceed. If the measurements differ,
		remeasure.
	</p>
	<h3>Identify Frame Step</h3>

	<p>
		Some windows have more than one frame step, while other windows have just a
		single, flat surface. This example shows two frame steps, <strong
			class="text-indow-blue">(1) in blue</strong
		>
		and
		<strong class="text-indow-yellow">(2) in yellow</strong>. For each window,
		you will be taking a set of 6 measurements which must be taken on the same
		frame step. A compatible frame step must have:
	</p>
	<ul class="w-auto">
		<li>5/8" of flat space</li>
		<li>At least 1/2" of space from window glass</li>
		<li>
			No obstructions such as blinds, shutters, shades, handles, cranks, or
			locks
		</li>
		<li>
			1 1/4" behind the blinds at the top of the frame, if installing with
			blinds present
		</li>
	</ul>

	<p>Use a measuring tape to check for the above minimums.</p>

	<p>
		<strong>Note:</strong> For noise reduction, the better frame step is at
		least 1”- 4” away from the window glass. See
		<a href="#appendix">appendix</a>
		or
		<a
			rel="external"
			target="_blank"
			href="https://www.youtube.com/watch?v=WPNWItsrqMU">watch this video</a
		>
		for more help.
	</p>
	<Image
		src={fromGlass}
		alt="Diagram shows which of two Frame Steps to use."
		class="no-bleed"
	/>
</Section>

<Grid row={1} columns={2} class="col-span-full! m-0! gap-0!">
	<Section
		backgroundColor="grey-light"
		class="place-content-between justify-between py-0!"
	>
		<Image
			src={howtomeasurewithlaser}
			class="w-full"
			alt="Diagram shows the incorrect and correct way to position the laser measurement tool in a window frame."
		/>
		<Image
			src={laserToolValue}
			class="max-w-64 justify-self-end"
			alt="Image of a Laser Measurement Tool."
		/>
	</Section>

	<Section class="content-start" id="how-to-use-the-laser-tool">
		<h2 class="md:place-self-start md:text-left">How to Use the Laser Tool</h2>
		<p class="md:place-self-start md:text-left">
			Take out the laser. (Item 1 in the kit.) You only need to use the red ON
			button and the CLEAR/OFF button.
		</p>
		<ol class="list-[upper-alpha]">
			<li>
				<p>
					Place the base of the Laser Measure Tool (side opposite laser beam) on
					the frame step of the side you are measuring.
				</p>
			</li>
			<li>
				<p>
					Press the red ON button, identified by the jewel, to turn on the
					laser. Keep your eye on the laser beam to make sure it doesn’t move
					off the frame step where the insert will be installed. The raised
					jewel will help you identify the ON button while staying focused on
					the laser beam.
				</p>
			</li>
			<li>
				<p>
					Press the red ON button again to take your first measurement. Your
					measurement will appear on the screen displayed in inches and
					fractions. If your laser does not automatically display inches and
					fractions, see appendix <a href="#appendix-laser-measure-tool-mode"
						>Laser Measure Tool Mode</a
					> before measuring.
				</p>
			</li>
			<li>
				<p>Press the red ON button again to reactivate the laser beam.</p>
			</li>
			<li>
				<p>
					Press the red ON button a fourth time to take a second measurement,
					which will appear on the laser’s screen in larger text below the first
					measurement. If the numbers match, record your measurement. If the
					measurements are within 1/16" of each other, that’s okay too - just
					use the larger of the two measurements. If they are more than 1/16"
					apart, keep measuring until they are within 1/16th of an inch of each
					other*.
				</p>
			</li>
		</ol>

		<p class="md:place-self-start md:text-left">
			Record measurements exactly as they are shown on the laser onto the
			Measure Form, e.g.: 55 9/32. Write the whole number and the fraction with
			no additional symbols.
		</p>

		<h3 class="md:place-self-start md:text-left">Need Help?</h3>

		<p class="md:place-self-start md:text-left">
			Check out our "Your Window With A Laser Tool" <a
				href="https://www.youtube.com/watch?v=Y3E8dnh-bow"
				rel="external"
				target="_blank"
			>
				video guide</a
			>
			or schedule a <a href="/measure-support">Measure Support</a> call.
		</p>
		<p class="md:place-self-start md:text-left">
			*See <a href="#appendix">appendix</a> for full explanation of frame depth and
			fraction chart.
		</p>
	</Section>
</Grid>

<Section id="measure-windows">
	<h2>Measuring Top & Bottom Widths</h2>
	<Image
		src={AWidthTop}
		alt="Diagram shows a person measuring the 'A' Width Top."
		class="no-bleed"
	/>
	<h3>A Width Top</h3>
	<p>
		Locate the frame step the inserts will be installed on. Place the base of
		the laser tool against the frame (top left) where the insert is going to be
		installed, and point the laser beam straight across the window frame (top
		right). It’s often best for the laser to touch the top and left side of the
		window, but the tool can face out or down as long as it lies flat against
		the frame and the laser dot falls where the insert will be installed. Take
		your measurements.
	</p>
	<p>
		<strong>Note:</strong> The laser captures its measurements from the base of the
		unit, so it’s critical the base sits flat against the frame to get accurate height
		and width measurements.
	</p>
	<p>
		Write the measurement on your <strong>Measure Form</strong> under the column
		<strong>‘A Width Top’</strong>. Record the fractional measurements exactly
		as your laser displays them. Measure twice! Until you have at least two
		measurements within 1/16" of each other.
	</p>

	<Image
		src={BWidthTop}
		alt="Diagram shows a person measuring the 'B' Width Bottom."
		class="no-bleed"
	/>
	<h3>B Width Bottom</h3>
	<p>
		Use the same frame step as before. Press the base of the laser flat against
		the bottom left side of your window frame with the laser’s beam straight
		across as seen in the diagram. Record your measurements on <strong
			>‘B Width Bottom’</strong
		>
		on the <strong>Measure Form</strong>. Measure twice! Until you have at least
		two measurements within 1/16" of each other.
	</p>
	<p>
		<strong>Tip:</strong> Not seeing fractions? See appendix
		<a href="#appendix-laser-measure-tool-mode">Laser Measure Tool Mode</a>. We
		recommend you completely measure each window twice and record on two rows on
		the form. If the rows match or each measurement is within 1/16", scratch one
		row out.
	</p>
</Section>

<Section id="measure-diagonals">
	<!-- <h2>Measuring Left &amp; Right Heights</h2> -->
	<Image
		src={CDWidth}
		alt="Diagram shows a person measuring the 'C' Height Left and 'D' Height Right."
		class="no-bleed"
	/>
	<h3>C Height Left</h3>
	<p>
		Using the same frame step as before, place the laser in the bottom left of
		the window frame pointed straight up as seen in the diagram to measure the
		height of the frame’s left side. Measure twice! Write these measurements on
		your <strong>Measure Form</strong> under <strong>‘C Height Left’</strong>.
	</p>

	<h3>D Height Right</h3>
	<p>
		Now do the same height measurement from the bottom right of the window
		frame, inputting your measurements into <strong>‘D Height Right’</strong> on
		the <strong>Measure Form</strong>.
	</p>
	<p>
		<strong>Tip:</strong> Textured surface or a sloped frame causing issues? Mark
		where the insert will be installed with tape to have a target for the laser.
	</p>
</Section>

<Section backgroundColor="grey-light" id="measure-diagonals">
	<h2>Measuring Diagonals</h2>
	<p>
		Take out the <strong>Adjustable Diagonal Extension Tool</strong> (item 6 in the
		kit). This stabilizes the Laser Measure Tool against the window frame while you
		take the diagonal measurements. These diagonal measurements are critical for a
		snug fit of your Indow inserts.
	</p>
	<Image
		src={measureDiagonalTool}
		alt="Diagram shows how to attach the Laser Measurement Tool to the Adjustable Diagonal Extension Tool."
		class="no-bleed"
	/>
	<p>
		Think of measuring the diagonals like an arcade game; timing and patience
		will help you win. Extend the stabilizing leg of the <strong
			>Adjustable Diagonal Extension Tool</strong
		> outward and adjust the tension on the bolt to stabilize the leg. Magnetically
		connect the laser to the face of the tool so the magnets snap together.
	</p>
	<Image
		src={measureDiagonal}
		alt="Diagram shows how the Adjustable Diagonal Extension Tool can be used."
		class="no-bleed"
	/>
	<p>
		Check for any paint buildup, textured sheetrock, corner gaps, or rounded-off
		areas in the joints of your frame step. If these are present, take more
		measurements and look for the longest distance keeping curves and gaps in
		the frame in mind.
	</p>
	<h3>E Diagonal Left</h3>

	<p>
		Place the tail of the extension tool in the bottom left joint of the frame
		step. Adjust the tool’s stabilizing leg along the frame until the laser beam
		rests in the opposite upper right joint of the frame as seen in the diagram.
		Keep an eye on the beam to ensure it doesn’t move off the frame step while
		your other hand is ready to press the red ON button. When the laser dot is
		in the joint, and equally on the top and side of the frame, take your
		measurements. Measure twice! Until you have at least two measurements within
		1/16" of each other. Write the measurement under <strong
			>‘E Diagonal Left’</strong
		> on the form.
	</p>
	<p>
		<strong>Note:</strong> The joint is where the two sides of the window frame meet.
		When you measure from joint to opposing joint, still measure to and from the frame
		step where your insert will be installed.
	</p>
	<p>
		<strong>Note:</strong> Record the fractional measurements exactly as they
		appear on the laser’s display. The window opening is larger than the laser
		shows, however the length of the extension tail is automatically added in
		the <strong>Measure by Indow</strong>
		portal.
	</p>

	<h3>F Diagonal Right</h3>
	<p>
		Repeat instructions for <strong>E Diagonal Left</strong>, but with the laser
		and extension tool in the bottom right joint of the window frame pointed
		toward the top left joint. Measure the diagonal of the right side of the
		frame and write under <strong>‘F Diagonal Right’</strong> on the Measure Form.
	</p>
	<p>
		<strong>Tip:</strong> Be careful! The laser dot moves easily. Want some
		additional tips? See appendix
		<a href="#appendix-diagonal-tool-tips">Diagonal Tool Tips</a>.
	</p>
	<div
		class="
    flex w-full flex-col gap-16
    sm:flex-row
  "
	>
		<div class="w-full">
			<Image
				src={EDiagonalLeft}
				class="
      mb-4
      max-md:max-w-sm
    "
				alt="Diagram shows a person measuring the 'E' Diagonal Left."
			/>
			<h3>E Diagonal Left</h3>
		</div>
		<div class="w-full">
			<Image
				src={FDiagonalRight}
				class="
      mb-4
      max-md:max-w-sm
    "
				alt="Diagram shows a person measuring the '
			
			F' Diagonal Left."
			/>
			<h3>F Diagonal Right</h3>
		</div>
	</div>
</Section>

<Section id="final-window-check">
	<h2>Final Window Check</h2>
	<p>
		Any number of reasons can cause a frame to curve or bow over time. There are
		convex (outward) bows and concave (inward) bows. Checking for bows ensures
		your inserts will fit snugly within the frames.
	</p>

	<h3>Check for Bows</h3>
	<p>
		Convex: Stretch a tape measure from corner to corner on one side. Pull it
		tight to see if there’s a point where the tape isn’t touching the frame (see
		convex diagram). If you see a gap, the frame has an outward convex bow.
		Check all four sides of each window.
	</p>
	<p>
		Concave: Stretch the tape measure across the face of the frame to see if any
		points of the frame dip toward the window's interior or pass over the tape
		(see concave diagram).
	</p>
	<p>
		<strong>Note:</strong> You can use string, a level, or other straightedge tool
		if you’d prefer.
	</p>
	<div
		class="
    mb-8 grid w-full gap-x-4 gap-y-16
    md:grid-cols-3
  "
	>
		<div>
			<Image
				src={bowNone}
				class="max-w-sm"
				alt="Diagram shows a window with a Convex bowed frame."
			/>
			<h3>This window has no bow.</h3>
		</div>
		<div>
			<Image
				src={bowConvex}
				class="max-w-sm"
				alt="Diagram shows a window with a Convex bowed frame."
			/>
			<h3>This window has an outward or convex bowed frame.</h3>
		</div>
		<div class="grid-cols-subgrid">
			<Image
				src={bowConcave}
				class="max-w-sm"
				alt="Diagram shows a window with a Concave bowed frame."
			/>
			<h3>This window has an inward or concave bowed frame.</h3>
		</div>
	</div>

	<p>
		If you detect a gap thicker than a pencil tip, follow the directions below.
		If you don’t detect any bows, continue to <strong
			>Submit Measurements Online</strong
		>.
	</p>
	<p>
		To measure the height of an inward or outward bow in a frame, tape both ends
		of the tape measure to the corners so the tape is taut, and use a ruler to
		measure the height of the bow where the gap is largest. Alternatively,
		someone can help measure the bow height while you hold the tape measure
		taut.
	</p>
	<p>
		If the frame is bowed more than ⅛" (the thickness of a pencil tip), record
		which side of the frame is bowed – the direction (inward or outward) and the
		bow height – in the “Notes” section of the <strong>Measure Form</strong>. Be
		sure to include where on the frame it’s highest and the start and stop
		location of the bow in inches. If the bow is on the bottom or top, provide
		the distance from the left where the gap is largest.
	</p>
	<p>
		<strong>Example:</strong> The frame has a 9/32" high inward gap on the left side,
		three inches from the bottom left hand corner of the sill.
	</p>
	<Image
		src={gapValue}
		alt="Diagram shows a window with a quarter inch gap at the top of the window frame."
		class="no-bleed"
	/>
</Section>

<Section id="submit-measurements">
	<h2>Submit Measurements Online</h2>
	<p>
		It’s time to enter your measurements into the <strong
			>Measure by Indow</strong
		> portal!
	</p>
	<p>
		Can't find the link to access your Measure By Indow portal? Contact <a
			href="/measure-support"
		>
			Measure Support.
		</a>
	</p>
	<p>
		Check your email for your <strong>Measure by Indow</strong> link to access the
		portal, subject line includes “Measure by Indow”.
	</p>
	<p>
		Click ADD WINDOW and add the measurements for your windows, one at a time.
		Follow along your <strong>Measure Form</strong>, selecting the larger
		measurement of the two within 1/16" of each other, as you enter:
	</p>
	<ul class="w-auto">
		<li>Product Type</li>
		<li>Tubing Color</li>
		<li>Frame Step</li>
	</ul>
	<p>
		<strong>Shape:</strong> Select Rectangle. Only select Custom if working with
		a special shape such as an arch or octagon. For custom-shaped windows,
		please refer to the
		<strong>Custom Order Form.</strong>
	</p>
	<p>
		Enter your measurements as they appear on your <strong>Measure Form</strong
		>. Type the whole number in the space provided. Type the top number of the
		fraction* in the field provided. Use the drop down to select the bottom part
		of the fraction. Do not enter any additional symbols or spaces. See example:
	</p>
	<Image
		src={measureForm}
		class="max-w-sm"
		alt="Sample image of Indow windows online Measurement Form."
	/>
	<p>
		<strong>Tip:</strong> If you are entering a whole number, the fraction section
		will automatically show “0/2”. There is no need to edit.
	</p>

	<p>
		<strong>Notes section</strong>: If you found bows, add description here.
		Note which side is bowed; the direction (inward/concave or outward/convex)
		as well as the height of the bow.
	</p>
	<p>
		Review your information before you submit for review. You can always come
		back to add more windows if you want to start with a few now.
	</p>
	<p>
		Watch out for typos! Having trouble resolving a red or yellow issue? Select
		the “schedule a support call” option.
	</p>
	<p>
		Everything entered and all errors resolved? You’re now ready to submit your
		order.
	</p>
	<p>
		<strong
			>Don’t send your Laser Measuring Kit back until your order has been
			reviewed and confirmed by our experts and payment has been submitted.</strong
		>
	</p>
	<p>
		Once we receive your measurements, our team will run further checks. When
		the assessment is complete and any issues have been resolved, we’ll send you
		a final invoice. You’ll have a chance to review your order before making the
		final payment.
	</p>
	<p>
		<strong>Important Note:</strong> If you feel any items in your order need
		attention, contact your <strong>Fit Specialist</strong> before you proceed.
		Remember, our Snug Fit Guarantee applies to measurements submitted through
		Measure by Indow as long as certain conditions are met, such as using Indow
		measurement tools, resolving all errors, and reporting all irregularities
		(i.e.: bows) in your Measure By Indow portal. For a full list, visit:
		<a href="/warranties/snug-fit-guarantee">here</a>.
	</p>
</Section>

<Section backgroundColor="blue">
	<Image
		src={indowFigure}
		class="max-w-80"
		alt="Dave is happy your measurement has been accepted."
	/>
	<h2>Nice Work! Measurement Accepted</h2>
	<p class="text-black">
		<strong>Tip:</strong> Can’t find your link to access
		<strong>Measure by Indow?</strong>
		<br />Go to: <a href="/measure-support">measure support </a>
	</p>
</Section>

<Section>
	<h2>Return the Laser Measuring Kit</h2>
	<p>
		Once an Indow representative has approved your submitted measurements,
		you’ll get an email from us so you can confirm your order. Then it’s time to
		return the kit. Apply the included return shipping label over the existing
		shipping label and mail the kit back to us in the USPS box in which it
		arrived.
	</p>
	<strong> Remember we can’t ship your order until we receive the kit. </strong>
	<Image
		src={returnLaserKit}
		class="no-bleed my-6"
		alt="Diagram of the USPS return label and return box for the Laser Measuring Kit."
	/>
	<strong>Expect an email from us soon on next steps!</strong>
</Section>

<Section
	class="
   content-start
   [&_h2]:md:place-self-start [&_h2]:md:text-left
   [&_h3]:md:place-self-start [&_h3]:md:text-left
   [&_p]:md:place-self-start [&_p]:md:text-left
 "
	id="appendix"
>
	<h2>Appendix</h2>
	<Grid row={1} columns={2} class="m-0! gap-x-6! gap-y-0!">
		<div>
			<h3>Tips For Using the Laser Tool</h3>
			<ul
				class="
      list-none
      [&_li>p]:pl-4
    "
			>
				<li>
					<h4
						id="appendix-diagonal-tool-tips"
						class="text-left text-indow-grey-dark normal-case"
					>
						Diagonal Tool Tips
					</h4>
					<p>
						See our <a href="/about-indow/care/measuring-diagonals"
							>Diagonal Guide</a
						>.
					</p>
					<ul>
						<li>
							<p>
								Hold the bottom to create resistance - the legs don’t have
								rubber so they can be adjusted easily.
							</p>
						</li>
						<li>
							<p>
								Use two hands on the tool to keep it steady when pressing the
								button.
							</p>
						</li>
						<li>
							<p>
								Place a book in front of the stabilizing (black) leg to keep it
								steady and prevent sliding.
							</p>
						</li>
						<li>
							<p>
								Hold your finger on the red measuring button and keep an eye on
								the laser dot.
							</p>
						</li>
						<li>
							<p>
								Enter dimensions displayed on the laser screen. Our system
								accounts for the length of the extension tail.
							</p>
						</li>
						<li>
							<p>
								Do not tilt the laser towards you or the glass. It should be
								measuring where the insert will be installed.
							</p>
						</li>
					</ul>
				</li>
				<li>
					<h4 class="text-left text-indow-grey-dark normal-case">
						Sloped Frame Tips
					</h4>
					<p>
						If the frame slopes inward toward the glass or outward away from the
						glass, it can pose installation issues. Use these tips or ask your
						Fit Specialist if you have compatibility questions.
					</p>
					<p>
						Your frame cannot have a slope angle that exceeds 15°, but if any
						slope is present, you should follow these steps. You also need a
						frame depth of at least ¾".
					</p>
					<ul>
						<li>
							<p>
								Check for slope angle with level, default iPhone measure app, or
								Android measure app (search for “angle meter”).
							</p>
						</li>
						<li>
							<p>
								Measure in from the front of your frame to where you want to
								install your insert on all four joints and mark with tape.
							</p>
						</li>
						<li>
							<p>
								Use these markings when measuring: for where to place laser tool
								and where to aim laser dot.
							</p>
						</li>
						<li>
							<p>
								If your frame depth is ¾" - 1", record the location and degree
								of the slope in the notes field in Measure by Indow when placing
								your order.
							</p>
						</li>
					</ul>
				</li>
			</ul>

			<h3>Get to Know Your Window</h3>
			<Image
				src={knowyourWindow}
				alt="Diagram shows a window and a list of window terms."
			/>

			<h3>Window Terms</h3>
			<ul
				class="
      list-none
      [&_li>p]:pl-4
    "
			>
				<li>
					<h4 class="text-left text-indow-grey-dark normal-case">Joint</h4>
					<p>
						Where two sides or inside edges of the window frame meet. You might
						think of these as the corners of the window, but they are not 3D
						because they do not include the glass pane as a side.
					</p>
				</li>
				<li>
					<h4 class="text-left text-indow-grey-dark normal-case">Muntin</h4>
					<p>
						See our:
						<a href="/guides/how-to-measure-for-mullions">Mullion Guide</a>.
					</p>
					<p>
						A bar of wood or metal separating and supporting panes of glass in
						window.
					</p>
				</li>

				<li>
					<h4 class="text-left text-indow-grey-dark normal-case">Sash</h4>
					<p>
						Part of the window that holds the panes. It fits inside the window
						frame and moves inside of it (when the sash is operable). Sashes
						vary depending on the type of window. For example, sometimes instead
						of sliding up along the frame, they tilt in or out.
					</p>
				</li>
				<li>
					<h4 class="text-left text-indow-grey-dark normal-case">Mullion</h4>
					<p>
						A metal H-shaped bar stationed in the window frame to divide a
						window into smaller units either vertically or horizontally. Window
						inserts require mullions for support when placed in extremely large
						window frames (using two window inserts to cover one opening). View
						our <a href="/guides/how-to-measure-for-mullions">Mullion</a> Guide here
						or connect with your Fit Specialist if you believe you need to add Mullions
						to your order.
					</p>
				</li>
			</ul>

			<h3>Window Hardware</h3>
			<p>
				Some windows, especially those with a sash, will have hardware which may
				protrude into one of the frame steps.
			</p>
			<Image
				src={windowHardware}
				alt="Diagram shows a window and a list of window hardware."
			/>

			<h3>Operable Window</h3>
			<p>
				An operable window is one that can be opened. Some, usually older,
				windows have been sealed shut.
			</p>

			<h3>Measurement Map</h3>
			<Image src={measurementMap} alt="Diagram shows a Measurement Map." />
		</div>
		<div>
			<h3>Measurement Terms</h3>

			<ul
				class="
      list-none
      [&_li>p]:pl-4
    "
			>
				<li>
					<h4 class="text-left text-indow-grey-dark normal-case">Frame Step</h4>
					<p>
						The flat surfaces within a window frame. This is what the
						compression tubing of your window insert will press against. Some
						windows have just one frame step, some have more. Some frame steps
						are interrupted by a lower sash or hardware.
					</p>
					<p>
						A compatible frame step has to be free of any obstructions fully
						exposed and continuous to have an Indow insert installed. It must
						also have a frame depth of at least 5/8". Check out our <a
							rel="external"
							target="_blank"
							href="https://www.youtube.com/watch?v=WPNWItsrqMU"
							>Frame Step video</a
						> for more information.
					</p>
				</li>
				<li>
					<h4 class="text-left text-indow-grey-dark normal-case">
						Frame Depth
					</h4>

					<p>
						How deep your frame step is, measured from front to back. You can
						measure it with a tape measure or ruler. You just need an
						approximation for your order:
					</p>
					<Image
						src={frameDepth}
						alt="Diagram shows two Frame Steps and Frame Depth of the second Frame Step."
					/>
				</li>
			</ul>
			<p>
				<strong>Note:</strong> If you are installing your insert where blinds are
				present, you will need a frame depth of at least 1 1/4" behind the blinds
				on the top of your window frame.
			</p>

			<h3 id="appendix-laser-measure-tool-mode">Laser Measure Tool Mode</h3>
			<p>
				There are multiple modes our laser tool can be in. It should arrive in:
				“Inches and Fractions” mode as shown on page 5.
			</p>
			<p>
				If the laser is not in the correct mode: turn the laser on, hold the
				grey second button down for 2 seconds. It should beep. Repeat this until
				you see “in” near the bottom left corner as shown in the diagram on page
				5.
			</p>
			<Image
				src={inchesAndfeet}
				class="mb-8"
				alt="Diagram shows the Laser Measurement Tool in inches and feet mode."
			/>
			<Image
				src={inchesOnly}
				class="mb-8"
				alt="Diagram shows the Laser Measurement Tool in inches only mode."
			/>

			<h3>Fraction Chart</h3>
			<ul
				class="
      grid gap-4 p-0
      xs:grid-flow-col xs:grid-cols-2 xs:grid-rows-16
      [&_li]:grid [&_li]:grid-cols-[auto_0.3fr_1fr] [&_li]:items-center
      [&_li_p]:text-lg
      [&_li>div]:grid [&_li>div]:aspect-square [&_li>div]:h-16 [&_li>div]:w-min
      [&_li>div]:justify-center [&_li>div]:rounded-full [&_li>div]:border-2
      [&_li>div]:border-solid [&_li>div]:border-indow-grey-dark
      [&_li>div]:bg-indow-blue [&_li>div]:text-indow-grey-dark
      [&_li>div>p]:place-self-center [&_li>div>p]:self-center
      [&_li>div>p]:font-bold [&_li>div>p]:text-indow-grey-dark
      [&_li>hr]:my-auto [&_li>hr]:border-2 [&_li>hr]:border-indow-grey-dark
      [&_li>p]:my-auto [&_li>p]:justify-self-start [&_li>p]:pl-2
    "
			>
				<li>
					<div>
						<p><sup>1</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.03125</p>
				</li>
				<li>
					<div>
						<p><sup>1</sup>&frasl;<sub>16</sub></p>
					</div>
					<hr />
					<p>0.0625</p>
				</li>
				<li>
					<div>
						<p><sup>3</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.09375</p>
				</li>
				<li>
					<div>
						<p><sup>1</sup>&frasl;<sub>8</sub></p>
					</div>
					<hr />
					<p>0.125</p>
				</li>
				<li>
					<div>
						<p><sup>5</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.15625</p>
				</li>
				<li>
					<div>
						<p><sup>3</sup>&frasl;<sub>16</sub></p>
					</div>
					<hr />
					<p>0.1875</p>
				</li>
				<li>
					<div>
						<p><sup>7</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.21875</p>
				</li>
				<li>
					<div>
						<p><sup>1</sup>&frasl;<sub>4</sub></p>
					</div>
					<hr />
					<p>0.250</p>
				</li>
				<li>
					<div>
						<p><sup>9</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.28125</p>
				</li>
				<li>
					<div>
						<p><sup>5</sup>&frasl;<sub>16</sub></p>
					</div>
					<hr />
					<p>0.3125</p>
				</li>
				<li>
					<div>
						<p><sup>11</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.34375</p>
				</li>
				<li>
					<div>
						<p><sup>3</sup>&frasl;<sub>8</sub></p>
					</div>
					<hr />
					<p>0.375</p>
				</li>
				<li>
					<div>
						<p><sup>13</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.40625</p>
				</li>
				<li>
					<div>
						<p><sup>7</sup>&frasl;<sub>16</sub></p>
					</div>
					<hr />
					<p>0.4375</p>
				</li>
				<li>
					<div>
						<p><sup>15</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.46875</p>
				</li>
				<li>
					<div>
						<p><sup>1</sup>&frasl;<sub>2</sub></p>
					</div>
					<hr />
					<p>0.500</p>
				</li>
				<li>
					<div>
						<p><sup>17</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.53125</p>
				</li>
				<li>
					<div>
						<p><sup>9</sup>&frasl;<sub>16</sub></p>
					</div>
					<hr />
					<p>0.5625</p>
				</li>
				<li>
					<div>
						<p><sup>19</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.59375</p>
				</li>
				<li>
					<div>
						<p><sup>5</sup>&frasl;<sub>8</sub></p>
					</div>
					<hr />
					<p>0.625</p>
				</li>
				<li>
					<div>
						<p><sup>21</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.65625</p>
				</li>
				<li>
					<div>
						<p><sup>11</sup>&frasl;<sub>16</sub></p>
					</div>
					<hr />
					<p>0.6875</p>
				</li>
				<li>
					<div>
						<p><sup>23</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.71875</p>
				</li>
				<li>
					<div>
						<p><sup>3</sup>&frasl;<sub>4</sub></p>
					</div>
					<hr />
					<p>0.750</p>
				</li>
				<li>
					<div>
						<p><sup>25</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.78125</p>
				</li>
				<li>
					<div>
						<p><sup>13</sup>&frasl;<sub>16</sub></p>
					</div>
					<hr />
					<p>0.8125</p>
				</li>
				<li>
					<div>
						<p><sup>27</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.84375</p>
				</li>
				<li>
					<div>
						<p><sup>7</sup>&frasl;<sub>8</sub></p>
					</div>
					<hr />
					<p>0.875</p>
				</li>
				<li>
					<div>
						<p><sup>29</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.90625</p>
				</li>
				<li>
					<div>
						<p><sup>15</sup>&frasl;<sub>16</sub></p>
					</div>
					<hr />
					<p>0.9375</p>
				</li>
				<li>
					<div>
						<p><sup>31</sup>&frasl;<sub>32</sub></p>
					</div>
					<hr />
					<p>0.96875</p>
				</li>
				<li>
					<div>
						<p>1</p>
					</div>
					<hr />
					<p>1.000</p>
				</li>
			</ul>
		</div>
	</Grid>
</Section>

<Reviews />
